import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { updateAgent, type Agent } from './api';

type ComplianceConfig = {
  report_drive_folder_id?: string;
  block_score_threshold?: number;
  review_score_threshold?: number;
};

const DEFAULT_BLOCK = 90;
const DEFAULT_REVIEW = 70;

export function ComplianceConfigForm({ orgId, agent }: { orgId: string; agent: Agent }) {
  const qc = useQueryClient();
  const cfg = (agent.config as ComplianceConfig) ?? {};

  const [folderId, setFolderId] = useState(cfg.report_drive_folder_id ?? '');
  const [blockTh, setBlockTh] = useState(String(cfg.block_score_threshold ?? DEFAULT_BLOCK));
  const [reviewTh, setReviewTh] = useState(String(cfg.review_score_threshold ?? DEFAULT_REVIEW));
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setFolderId(cfg.report_drive_folder_id ?? '');
    setBlockTh(String(cfg.block_score_threshold ?? DEFAULT_BLOCK));
    setReviewTh(String(cfg.review_score_threshold ?? DEFAULT_REVIEW));
    setSaved(false);
    setErr(null);
  }, [agent.id]);

  const save = useMutation({
    mutationFn: () => {
      const block = parseInt(blockTh, 10);
      const review = parseInt(reviewTh, 10);
      if (Number.isNaN(block) || block < 1 || block > 100) {
        throw new Error('block_score_threshold must be 1-100');
      }
      if (Number.isNaN(review) || review < 0 || review >= block) {
        throw new Error('review_score_threshold must be 0-99 and lower than block');
      }
      const next: ComplianceConfig = {
        report_drive_folder_id: folderId.trim() || undefined,
        block_score_threshold: block,
        review_score_threshold: review,
      };
      return updateAgent(orgId, agent.id, { config: next });
    },
    onSuccess: () => {
      setSaved(true);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['agent', orgId, agent.id] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: Error) => setErr(e.message),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <Card>
      <CardHeader
        title="Compliance policy"
        description="Where reports go and when to require a human reviewer. The set of providers (mock today, Chainalysis/SEON later) is controlled via the Connectors bound to this agent."
      />
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-5">
          <Section
            title="Drive folder for reports"
            hint="Drive folder id where every compliance report is uploaded. Leave empty to drop reports in the root of My Drive. Find the id in the Drive URL after /folders/."
          >
            <input
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              placeholder="1aB2cD3e4F5g6H7i8J9k0L"
              className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 font-mono text-sm"
            />
          </Section>

          <Section
            title="Routing thresholds"
            hint="The agent collects scoring signals from every provider connector, then calls compliance_decide which applies these numeric thresholds server-side. The aggregated score is the MAX across all signals — a single high-risk hit drives the verdict."
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-ink-secondary">
                  Block at score ≥ <span className="text-ink-tertiary">(default 90)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={blockTh}
                  onChange={(e) => setBlockTh(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm"
                />
                <p className="mt-1 text-[10px] text-ink-tertiary">
                  At or above this score → auto-blocked, no human review.
                </p>
              </div>
              <div>
                <label className="block text-xs text-ink-secondary">
                  Human review at score ≥ <span className="text-ink-tertiary">(default 70)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={reviewTh}
                  onChange={(e) => setReviewTh(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-line bg-surface-white px-2 py-1.5 text-sm"
                />
                <p className="mt-1 text-[10px] text-ink-tertiary">
                  At or above this score AND below block → routed to Reviews. Below → auto-approved.
                </p>
              </div>
            </div>
          </Section>

          {err && <p className="text-sm text-danger">{err}</p>}
          {save.error && !err && (
            <p className="text-sm text-danger">{(save.error as Error).message || 'Could not save'}</p>
          )}
          <div className="flex items-center justify-end gap-3">
            {saved && <p className="font-mono text-xs text-ok">Saved</p>}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-primary">{title}</label>
      {hint && <p className="mt-0.5 text-xs text-ink-tertiary">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}
